import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import api from '../services/api';
import { DEMO_STUDIO_URL } from '../config/apiBase';
import { captureAttribution, trackEvent } from '../utils/attribution';
import './Landing.css';

// Marketing page for studio owners, served on the bare platform domain
// (learnsalsa.online). Its one job is turning ad and outreach traffic into
// leads: explain the product, show it, and collect the studio's details.

interface LeadForm {
  name: string;
  email: string;
  studioName: string;
  location: string;
  studentCount: string;
  role: string;
  message: string;
  website: string; // honeypot, hidden from people
}

const FEATURES = [
  {
    title: 'Practice that sticks',
    body: 'Every move in your curriculum on video, with 0.5× slow motion, mirror mode and A–B looping, so students can drill the exact count they keep missing.',
    icon: 'M8 5v14l11-7z',
  },
  {
    title: 'Coaching between classes',
    body: 'Students record themselves and send it in. Instructors work through a review queue and reply with one concrete correction, no more lost WhatsApp videos.',
    icon: 'M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z',
  },
  {
    title: 'Your studio, your brand',
    body: 'Your own address (yourstudio.learnsalsa.online), logo and colors. Invite-only or open sign-up, with separate seats for owners, instructors and students.',
    icon: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10z',
  },
  {
    title: 'Live classes',
    body: 'Stream a class straight from the browser to members who can’t make it to the studio that night.',
    icon: 'M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z',
  },
];

const STEPS = [
  { title: 'Add your curriculum', body: 'Upload your moves and patterns, with breakdown clips from more than one angle.' },
  { title: 'Invite your people', body: 'Send invites to your instructors and students. They sign in on any phone or laptop.' },
  { title: 'Coach between classes', body: 'Students practice and send clips; your instructors reply from their review queue.' },
];

const LandingPage: React.FC = () => {
  const attribution = useMemo(() => captureAttribution(), []);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LeadForm>({ defaultValues: { role: 'owner', studentCount: '' } });

  useEffect(() => {
    document.title = 'LearnSalsa: practice & coaching app for dance studios';
  }, []);

  const onSubmit = async (data: LeadForm) => {
    setError(null);
    try {
      await api.post('/leads', { ...data, ...attribution });
      trackEvent('Lead');
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Something went wrong. Please try again in a moment.');
    }
  };

  const demoLink = (label: string, className: string) => (
    <a href={DEMO_STUDIO_URL} className={className} onClick={() => trackEvent('DemoClick')}>
      {label}
    </a>
  );

  return (
    <div className="lp">
      <header className="lp-header">
        <div className="lp-container lp-header-row">
          <a href="/" className="lp-logo">LearnSalsa</a>
          <nav className="lp-nav" aria-label="Main">
            <a href="#features" className="lp-nav-link">Features</a>
            <a href="#how" className="lp-nav-link">How it works</a>
            {demoLink('Live demo', 'lp-nav-link')}
            <a href="#contact" className="btn btn-primary btn-small">Talk to us</a>
          </nav>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-container lp-hero-grid">
            <div className="lp-hero-copy">
              <span className="lp-eyebrow">For salsa &amp; bachata studios</span>
              <h1>Your students keep learning between classes.</h1>
              <p className="lp-lede">
                LearnSalsa gives your studio its own practice app. Your moves on video with slow
                motion, mirroring and looping, and a feedback loop where students send clips and
                your instructors coach them, all under your studio’s name.
              </p>
              <div className="lp-cta-row">
                <a href="#contact" className="btn btn-primary lp-btn-lg">Book a walkthrough</a>
                {demoLink('Explore the live demo →', 'btn btn-secondary lp-btn-lg')}
              </div>
              <p className="lp-hero-note">No sign-up needed for the demo. Try it as a student, instructor or owner.</p>
            </div>
            <div className="lp-hero-visual">
              <figure className="lp-frame lp-frame--desktop">
                <img
                  src="/landing/student-move.jpg"
                  alt="A move page in LearnSalsa: video player with slow motion, A–B loop and mirror controls"
                  width={1600}
                  height={918}
                />
              </figure>
              <figure className="lp-frame lp-frame--phone">
                <img
                  src="/landing/student-mobile.jpg"
                  alt="The same move on a phone"
                  width={780}
                  height={1380}
                />
              </figure>
            </div>
          </div>
        </section>

        <section id="features" className="lp-section">
          <div className="lp-container">
            <h2 className="lp-section-title">Everything your classes don’t have time for</h2>
            <p className="lp-section-lede">
              An hour a week isn’t enough to lock in a pattern. LearnSalsa covers the other six days.
            </p>
            <div className="lp-features">
              {FEATURES.map((f) => (
                <article key={f.title} className="lp-feature">
                  <span className="lp-feature-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor"><path d={f.icon} /></svg>
                  </span>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section lp-section--alt">
          <div className="lp-container lp-split">
            <div className="lp-split-copy">
              <span className="lp-eyebrow">For instructors</span>
              <h2 className="lp-section-title">A review queue instead of a camera roll</h2>
              <p className="lp-section-lede">
                Every clip a student sends lands in one place, with how long they’ve been waiting.
                Watch it slowed down, reply with a correction, and they get it before next class.
              </p>
            </div>
            <figure className="lp-frame lp-frame--wide">
              <img
                src="/landing/instructor-review.jpg"
                alt="Instructor review queue showing four student clips waiting for feedback"
                width={1600}
                height={918}
                loading="lazy"
              />
            </figure>
          </div>
        </section>

        <section id="how" className="lp-section">
          <div className="lp-container">
            <h2 className="lp-section-title">How it works</h2>
            <ol className="lp-steps">
              {STEPS.map((s, i) => (
                <li key={s.title} className="lp-step">
                  <span className="lp-step-num">{i + 1}</span>
                  <h3>{s.title}</h3>
                  <p>{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="lp-section lp-section--alt">
          <div className="lp-container lp-demo-band">
            <div>
              <h2 className="lp-section-title">See it from every seat</h2>
              <p className="lp-section-lede">
                The demo studio is a full LearnSalsa space you can click around in. Log in as a
                student, an instructor or the owner. It resets every night.
              </p>
            </div>
            {demoLink('Open the demo studio →', 'btn btn-primary lp-btn-lg')}
          </div>
        </section>

        <section id="contact" className="lp-section">
          <div className="lp-container lp-contact">
            <div className="lp-contact-copy">
              <h2 className="lp-section-title">Bring LearnSalsa to your studio</h2>
              <p className="lp-section-lede">
                Tell us a little about your studio. We’ll get back to you within a day to set up a
                short walkthrough and talk through what you need.
              </p>
            </div>

            <div className="lp-form-card">
              {sent ? (
                <div className="lp-form-success" role="status">
                  <h3>Thanks, we got it!</h3>
                  <p>We’ll be in touch within a day. Meanwhile, have a look around the demo studio.</p>
                  {demoLink('Open the demo studio →', 'btn btn-secondary')}
                </div>
              ) : (
                <form className="lp-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                  <div className="lp-form-row">
                    <div className="lp-field">
                      <label htmlFor="lead-name">Your name</label>
                      <input id="lead-name" autoComplete="name" aria-invalid={!!errors.name}
                        {...register('name', { required: 'Please enter your name' })} />
                      {errors.name && <span className="lp-field-error">{errors.name.message}</span>}
                    </div>
                    <div className="lp-field">
                      <label htmlFor="lead-email">Email</label>
                      <input id="lead-email" type="email" autoComplete="email" aria-invalid={!!errors.email}
                        {...register('email', {
                          required: 'Please enter your email',
                          pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'That email doesn’t look right' },
                        })} />
                      {errors.email && <span className="lp-field-error">{errors.email.message}</span>}
                    </div>
                  </div>
                  <div className="lp-form-row">
                    <div className="lp-field">
                      <label htmlFor="lead-studio">Studio name</label>
                      <input id="lead-studio" autoComplete="organization" aria-invalid={!!errors.studioName}
                        {...register('studioName', { required: 'Please enter your studio’s name' })} />
                      {errors.studioName && <span className="lp-field-error">{errors.studioName.message}</span>}
                    </div>
                    <div className="lp-field">
                      <label htmlFor="lead-location">City <span className="lp-optional">(optional)</span></label>
                      <input id="lead-location" autoComplete="address-level2" {...register('location')} />
                    </div>
                  </div>
                  <div className="lp-form-row">
                    <div className="lp-field">
                      <label htmlFor="lead-count">Active students <span className="lp-optional">(optional)</span></label>
                      <select id="lead-count" {...register('studentCount')}>
                        <option value="">Choose…</option>
                        <option value="under-50">Under 50</option>
                        <option value="50-150">50–150</option>
                        <option value="150-500">150–500</option>
                        <option value="500+">500+</option>
                      </select>
                    </div>
                    <div className="lp-field">
                      <label htmlFor="lead-role">You are</label>
                      <select id="lead-role" {...register('role')}>
                        <option value="owner">The studio owner</option>
                        <option value="instructor">An instructor</option>
                        <option value="other">Something else</option>
                      </select>
                    </div>
                  </div>
                  <div className="lp-field">
                    <label htmlFor="lead-message">Anything we should know? <span className="lp-optional">(optional)</span></label>
                    <textarea id="lead-message" rows={3} {...register('message')} />
                  </div>
                  {/* Honeypot: off-screen and skipped by keyboard and screen readers */}
                  <div className="lp-hp" aria-hidden="true">
                    <label htmlFor="lead-website">Website</label>
                    <input id="lead-website" tabIndex={-1} autoComplete="off" {...register('website')} />
                  </div>
                  {error && <p className="lp-form-error" role="alert">{error}</p>}
                  <button type="submit" className="btn btn-primary lp-btn-lg lp-submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending…' : 'Request a walkthrough'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-container lp-footer-row">
          <span>© {new Date().getFullYear()} LearnSalsa</span>
          <span className="lp-footer-note">
            Screenshots are from the demo studio. Its dancers and videos are AI-generated samples.
          </span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
